import { useTranslation } from 'react-i18next';
import { Check, Clock, ExternalLink, Loader2, X } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui';
import type { DesignChannelPublication } from '@/api/designs';
import { describePublishFailReason } from '@/lib/api-errors';

interface ChannelStatusCellProps {
  publication?: DesignChannelPublication;
}

/**
 * One cell of the channel grid (plan U10).
 *
 * Empty means the listing was never sent here, which is a state the seller
 * acts on rather than an error. A cross carries the translated reason; a tick
 * carries the link to the product, because the target id is what the seller
 * wants next after a success.
 */
export function ChannelStatusCell({ publication }: ChannelStatusCellProps) {
  const { t } = useTranslation();

  if (!publication || publication.status === 'OPEN') {
    return (
      <span className="text-mute" aria-label={t('seller.designs.channel.open')}>
        –
      </span>
    );
  }

  if (publication.status === 'RUNNING') {
    return (
      <span
        className="inline-flex items-center gap-1 text-mute"
        aria-label={t('seller.designs.channel.running')}
      >
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      </span>
    );
  }

  // Pull stores only: the partner app fetched the product and has not said
  // yet what Shopify made of it. Not a success, so no tick and no link.
  if (publication.status === 'PULLED') {
    return (
      <span
        className="inline-flex items-center text-mute"
        aria-label={t('seller.designs.channel.pulled')}
        title={t('seller.designs.channel.pulled')}
      >
        <Clock className="h-4 w-4" aria-hidden="true" />
      </span>
    );
  }

  if (publication.status === 'PUBLISHED') {
    const link = publication.items.find((item) => item.targetUrl)?.targetUrl;

    if (!link) {
      // The label sits on the span, not the icon: an svg with aria-label is
      // not reliably exposed as a labelled element.
      return (
        <span
          className="inline-flex text-success"
          aria-label={t('seller.designs.channel.published')}
        >
          <Check className="h-4 w-4" aria-hidden="true" />
        </span>
      );
    }

    return (
      <a
        href={link}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 text-success hover:underline"
        aria-label={t('seller.designs.channel.openInShop')}
      >
        <Check className="h-4 w-4" aria-hidden="true" />
        <ExternalLink className="h-3 w-3" aria-hidden="true" />
      </a>
    );
  }

  // Falls back to the raw code rather than nothing: an untranslated code is
  // still more useful to the seller than an empty tooltip.
  const reason =
    describePublishFailReason(publication.failReasonCode) ||
    t('seller.designs.channel.failedUnknown');

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center text-danger"
          aria-label={t('seller.designs.channel.failed')}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <span>{reason}</span>
        {publication.failReasonDetail && (
          <span className="mt-1 block text-mute">
            {publication.failReasonDetail}
          </span>
        )}
      </TooltipContent>
    </Tooltip>
  );
}
